import { Box, Grid, Typography } from '@mui/material';
import DetailColumn from '@shared/components/DetailColumn';
import { VisitDetail, VisitForm } from '@features/visits/entity';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import AppTheme from '@libs/mui/AppTheme';
import CaptionCard from '@shared/components/CaptionCard';
import SanitizerIcon from '@mui/icons-material/Sanitizer';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import { useRef } from 'react';
import { ImperativeFormDialogRefType } from '@shared/components/UserFormDialog';
import { UseFormReturn } from 'react-hook-form';
import VisitDetailStatusSwitch from './visit-detail-status-switch';
import Face4Icon from '@mui/icons-material/Face4';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import { Paths } from '@shared/AppRoutes';
import { useAppNavigate, useSubscriptionPlan } from '@shared/store';
import { To, NavigateOptions } from 'react-router';
import { SubscriptionPlansEnum } from '../../../../subscription/entity';
import VisitDetailEditDialog from './visit-detail-edit-dialog';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { getDateTimeFromUtcToLocal, formatToCZK } from '@shared/entity';
import { useFormatCurrency } from '@features/currency/currency.hooks';
import DepositStatusSwitch from './deposit-status-switch';

type VisitDetailGridProps = {
  visitData: VisitDetail;
  methods: UseFormReturn<VisitForm>;
};
const VisitDetailGrid = (props: VisitDetailGridProps) => {
  const { visitData, methods } = props;
  const { procedures } = visitData;
  const { t } = useIntlTranslations();
  const dialogRef = useRef<ImperativeFormDialogRefType | null>(null);
  const navigate = useAppNavigate();
  const plan = useSubscriptionPlan();
  const isExpertPlan = plan === SubscriptionPlansEnum.EXPERT;
  const { format: fmtCurrency } = useFormatCurrency();

  const { soldGoodsRevenue, recommendedPrice } = procedures.reduce(
    (total, procedure) => {
      if (procedure.type === 'SALE') {
        procedure.stockAllowances.forEach(stockAllowance => {
          const quantity = Number(stockAllowance.quantity);

          total.soldGoodsRevenue += Number(stockAllowance.avgUnitPrice) * quantity;

          total.recommendedPrice += Number(stockAllowance.stockItem?.retailPrice ?? 0) * quantity;
        });
      }

      return total;
    },
    {
      soldGoodsRevenue: 0,
      recommendedPrice: 0,
    },
  );

  const visitDetailCells = getVisitDetailGridCells(
    visitData,
    soldGoodsRevenue,
    recommendedPrice,
    methods,
    navigate,
    isExpertPlan,
    fmtCurrency,
  );

  return (
    <>
      <VisitDetailEditDialog methods={methods} visit={visitData} dialogRef={dialogRef} />
      <Box
        sx={{
          position: 'relative',
          isolation: 'isolate',
        }}
      >
        {methods.watch('visitClosed') && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.34)',
              zIndex: 1,
              pointerEvents: 'auto',
              borderRadius: '3px',
              cursor: 'not-allowed',
            }}
          />
        )}
        <Grid container rowSpacing={1} columnSpacing={1}>
          {visitDetailCells.map((cell, index) => {
            if (!cell.shouldDisplay) {
              return null;
            }

            return (
              <Grid size={cell.gridSize} key={index}>
                {cell.isCaptionCard ? (
                  <CaptionCard
                    title={t(cell.label)}
                    caption={!!cell.note ? t(cell.note) : t(cell.getCaption(visitData))}
                    endElement={cell.endElement}
                    {...(cell.editable
                      ? { onClick: () => dialogRef.current?.open(cell.fieldName) }
                      : {})}
                  />
                ) : (
                  <DetailColumn
                    label={t(cell.label)}
                    {...(cell.editable
                      ? { onClick: () => dialogRef.current?.open(cell.fieldName) }
                      : cell.onClick
                        ? { onClick: cell.onClick }
                        : {})}
                    disabled={cell.disabled}
                    disabledMessage={cell.disabledMessage && t(cell.disabledMessage)}
                    endElement={cell.endElement}
                    zIndex={cell.zIndex}
                    iconBackgroundColor={cell.iconBackgroundColor}
                    icon={cell.icon}
                    input={cell.input}
                    helperText={cell.helperText && t(cell.helperText)}
                  />
                )}
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </>
  );
};

export default VisitDetailGrid;

const getVisitDetailGridCells = (
  visitData: VisitDetail,
  soldGoodsRevenue: number,
  recommendedPrice: number,
  methods: UseFormReturn<VisitForm>,
  navigate: {
    (to: To, options?: NavigateOptions): void;
    (delta: number): void;
  },
  isExpertPlan: boolean,
  fmtCurrency: (amount: number | string | undefined | null) => string,
) =>
  [
    {
      label: 'visitDetailGrid.customer',
      input: visitData.client.firstName + ' ' + visitData.client.lastName,
      helperText: visitData.client.deletedAt
        ? 'visitDetailGrid.clientDeleted'
        : 'visitDetailGrid.goToClientProfile',
      zIndex: 1,
      onClick: visitData.client.deletedAt
        ? undefined
        : () => navigate(Paths.clientDetail(visitData.client.id)),
      disabled: !!visitData.client.deletedAt,
      icon: (
        <Face4Icon fontSize="small" color={visitData.client.deletedAt ? 'disabled' : 'success'} />
      ),
      iconBackgroundColor: visitData.client.deletedAt
        ? AppTheme.palette.action.disabledBackground
        : AppTheme.palette.success.light,
      gridSize: 6,
      shouldDisplay: true,
    },
    {
      label: 'visitDetailGrid.service',
      iconBackgroundColor: AppTheme.palette.success.light,
      input: visitData.services.at(0)?.serviceName,
      helperText: 'visitDetailGrid.serviceHelperText',
      icon: <ContentCutIcon fontSize="small" color="success" />,
      gridSize: 6,
      fieldName: 'hairCutId',
      editable: true,
      shouldDisplay: true,
    },
    {
      label: 'visitDetailGrid.date',
      iconBackgroundColor: AppTheme.palette.info.light,
      input: getDateTimeFromUtcToLocal(visitData.visit.dateFrom),
      helperText: getDateTimeFromUtcToLocal(visitData.visit.dateTo),
      icon: <QueryBuilderIcon fontSize="small" />,
      gridSize: 6,
      editable: true,
      fieldName: 'date',
      shouldDisplay: true,
    },
    {
      label: 'visitDetailGrid.visitStatus',
      iconBackgroundColor: methods.watch('visitClosed')
        ? AppTheme.palette.success.light
        : AppTheme.palette.primary.light,
      zIndex: 1,
      endElement: <VisitDetailStatusSwitch methods={methods} visitDetail={visitData} />,
      input: (
        <Typography
          fontSize="0.9rem"
          textTransform="uppercase"
          color={methods.watch('visitClosed') ? 'success.main' : 'error.main'}
          fontWeight={600}
        >
          {methods.watch('visitClosed') ? 'Uzavřeno' : 'Nezavřeno'}
        </Typography>
      ),
      gridSize: 6,
      fieldName: 'visitStatus',
      shouldDisplay: true,
    },
    {
      label: 'visitDetailGrid.servicePricePaid',
      iconBackgroundColor: '#69eeff73',
      icon: <AttachMoneyIcon fontSize="small" />,
      input: fmtCurrency(
        (visitData.financials.paidPrice || 0) - (visitData.loyalty.loyaltyPointsUsed || 0),
      ),
      helperText: fmtCurrency(visitData.services[0].basePrice),
      gridSize: 6,
      editable: true,
      fieldName: 'paidPrice',
      shouldDisplay: true,
    },
    {
      label: 'visitDetailGrid.soldItemsRevenue',
      iconBackgroundColor: AppTheme.palette.primary.light,
      icon: <SanitizerIcon fontSize="small" color="primary" />,
      input: fmtCurrency(soldGoodsRevenue),
      helperText: fmtCurrency(recommendedPrice),
      gridSize: 6,
      shouldDisplay: true,
      disabled: !isExpertPlan,
      disabledMessage: 'visitDetailGrid.soldItemsRevenueDisabledMessage',
    },
    {
      label: 'visitDetailGrid.loyaltyPointsUsed',
      input: visitData.loyalty.loyaltyPointsUsed,
      iconBackgroundColor: AppTheme.palette.warning.light,
      icon: <LoyaltyIcon fontSize="small" sx={{ color: AppTheme.palette.warning.main }} />,
      gridSize: 6,
      editable: true,
      fieldName: 'loyaltyPointsUsed',
      shouldDisplay: Boolean(visitData.worker.loyaltyPercent),
      disabled: !isExpertPlan,
      disabledMessage: 'visitDetailGrid.soldItemsRevenueDisabledMessage',
    },
    {
      label: 'visitDetailGrid.depositState',
      getCaption: (visitData: VisitDetail) =>
        `${visitData.financials.depositStatus} - ${fmtCurrency(visitData.financials.deposit || 0)}`,
      endElement: <DepositStatusSwitch methods={methods} visitDetail={visitData} />,
      gridSize: 12,
      editable: true,
      fieldName: 'depositStatus',
      isCaptionCard: true,
      shouldDisplay: visitData.client.deposit,
    },
    {
      label: 'visitDetailGrid.clientDoesNotPayDeposit',
      getCaption: () => 'visitDetailGrid.changeOnClientProfile',
      gridSize: 12,
      isCaptionCard: true,
      shouldDisplay: !visitData.client.deposit,
    },
    {
      label: 'visitDetailGrid.visitInfo',
      shouldDisplay: true,
      getCaption: (visitData: VisitDetail) => 'visitDetailGrid.visitInfoMessage',
      note: visitData.visit.note,
      gridSize: 12,
      editable: true,
      fieldName: 'note',
      isCaptionCard: true,
    },
  ].filter(Boolean);
